import { type QueryKey, type UseBaseQueryOptions, type UseQueryResult, useQuery } from "@tanstack/react-query"
import { AxiosError } from "axios"
import isNumber from "lodash-es/isNumber"

import { hours, minutes, seconds } from "@/3514/utils"

import adapters from "./post-session.api.queries.adapters"
import fetchers from "./post-session.api.queries.fetchers"
import { EPostSessionQueryKey as EQueryKey } from "./post-session.modal.api.config"
import type {
    IPostSessionModuleReflectionQuestionsQueryDto as IModuleReflectionQuestionsQueryDto,
    IPostSessionModuleReflectionQuestionsQueryModel as IModuleReflectionQuestionsQueryModel,
    IPostSessionInitialDataDto,
    IPostSessionInitialDataModel
} from "./post-session.modal.api.types"

type TQuestionsQueryParams = { moduleId: number; participantId: number }
type TQuestionsQuery = UseQueryResult<IModuleReflectionQuestionsQueryModel, AxiosError>

type TInitialDataQueryParams = Pick<UseBaseQueryOptions, "enabled">
type TInitialDataQuery = UseQueryResult<IPostSessionInitialDataModel, AxiosError>

function useQuestionsQuery({
    participantId,
    moduleId
}: TQuestionsQueryParams): ReturnType<(params: TQuestionsQueryParams) => TQuestionsQuery> {
    return useQuery<IModuleReflectionQuestionsQueryDto, AxiosError, IModuleReflectionQuestionsQueryModel, QueryKey>({
        queryKey: EQueryKey.ModuleReflectionQuestions({ participantId, moduleId }),
        queryFn: fetchers.moduleReflectionQuestions,
        select: adapters.moduleReflectionQuestions,
        retry: 1,
        retryDelay: seconds(3),
        notifyOnChangeProps: ["data", "isFetching", "isError", "error", "promise"],
        staleTime: minutes(15),
        gcTime: minutes(10),
        networkMode: "offlineFirst",
        experimental_prefetchInRender: true,
        throwOnError: true,
        enabled: isNumber(moduleId)
    })
}

function useInitialDataQuery({
    enabled
}: TInitialDataQueryParams): ReturnType<(params: TInitialDataQueryParams) => TInitialDataQuery> {
    return useQuery<IPostSessionInitialDataDto, AxiosError, IPostSessionInitialDataModel, QueryKey>({
        queryKey: EQueryKey.ReflectionInitialData(),
        queryFn: fetchers.reflectionInitialData,
        select: adapters.reflectionInitialData,
        staleTime: hours(3),
        gcTime: minutes(5),
        retry: false,
        refetchIntervalInBackground: true,
        refetchInterval: hours(3),
        notifyOnChangeProps: ["data", "dataUpdatedAt", "isError"],
        networkMode: "offlineFirst",
        throwOnError: false,
        enabled,
        experimental_prefetchInRender: true
    })
}

export {
    useQuestionsQuery as usePostSessionModuleReflectionQuestionsQuery,
    useInitialDataQuery as usePostSessionInitialDataQuery
}

export type {
    TQuestionsQuery as TPostSessionModuleReflectionQuestionsQuery,
    TInitialDataQuery as TPostSessionInitialDataQuery
}
